import { type InferResponseType, unwrap, withSignal } from "@nexxt/api-client";
import { reportError } from "@nexxt/app-kit";
import type { CrmMoveKind, IssueCrmMoveLinkInput } from "@nexxt/contracts/crm";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { m } from "../../../paraglide/messages.js";
import { api } from "../../api";
import { todayQueries } from "./today.queries";

// Server state of acting on the signals (ADR-316 §2, §3, §5): "Hot right now", the moves of the signals and "Not
// now", and the toasts the own room of the person says to read. The moves sit under the keys of Today, so the room of
// Today reads them again with it.

const hot = api.crm.hot;
const moves = api.crm.moves;
const notices = api.crm.notices;

export type HotSignal = InferResponseType<typeof hot.$get, 200>["items"][number];
export type SignalMove = InferResponseType<typeof moves.$get, 200>["items"][number];
export type Notices = InferResponseType<typeof notices.$get, 200>;
export type Notice = Notices["items"][number];

/** Of one territory or of all, through the lens of some brands or of all, as Today. */
const lensQuery = (territory: string | undefined, brandOrgIds: string[] | undefined) => ({
  ...(territory && { territory }),
  ...(brandOrgIds && { brandOrgIds: brandOrgIds.join(",") }),
});

export const hotQueries = {
  all: ["hot"] as const,

  of: (territory: string | undefined, brandOrgIds: string[] | undefined) =>
    queryOptions({
      queryKey: [...hotQueries.all, { territory, brandOrgIds }],
      queryFn: ({ signal }) => unwrap(hot.$get({ query: lensQuery(territory, brandOrgIds) }, withSignal(signal))),
    }),

  moves: (territory: string | undefined, brandOrgIds: string[] | undefined) =>
    queryOptions({
      queryKey: [...todayQueries.all, "moves", { territory, brandOrgIds }],
      queryFn: ({ signal }) => unwrap(moves.$get({ query: lensQuery(territory, brandOrgIds) }, withSignal(signal))),
    }),
};

/** The toasts after `since`; without it, none and the cursor to start from. */
export const readNotices = (since: string | null): Promise<Notices> =>
  unwrap(notices.$get({ query: since ? { since } : {} }));

/**
 * `crm.move.issueLink`: "Copy personal link" on a recovery or a freed place (ADR-316 §4), the link once, never cached
 * (Q-SEC-40). The moves are not read again here: the card keeps the link until the person is done with it.
 */
export function useMoveLink() {
  return useMutation({
    // never kept by the cache of the mutations once the card is gone (Q-SEC-40)
    gcTime: 0,
    mutationFn: (input: IssueCrmMoveLinkInput) => unwrap(moves.link.$post({ json: input })),
    onError: (error) => reportError(error, m.home_move_snooze_failed),
  });
}

/** The moves read again, once the link of a move is copied and its card closed. */
export function useMovesAgain() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [...todayQueries.all, "moves"] });
}

/** `crm.move.snooze`: "Not now", and the moves read again without it. */
export function useSnoozeMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (move: { kind: CrmMoveKind; subjectId: string }) => unwrap(moves.snooze.$post({ json: move })),
    onError: (error) => reportError(error, m.home_move_snooze_failed),
    onSettled: () => qc.invalidateQueries({ queryKey: [...todayQueries.all, "moves"] }),
  });
}
